import { getSupabaseClient } from '@/integrations/supabase/client';
import { Sale, Tab } from '@/types';
import {
  EXTERNAL_ORDER_STATUSES,
  ExternalOrderStatus,
  isValidStatusTransition,
} from '@/constants/externalOrders';
import { EXTERNAL_ORDER_PAYMENT_METHOD } from '@/constants/defaultPaymentMethodContant';
import { salesService } from '@/services/sales/salesService';
import { tabsService } from '@/services/tabs/tabsService';
import {
  TAB_ITEM_MODIFIERS_EMBED,
  type TabItemModifierRow,
  mapTabItemModifierRows,
} from '@/services/products/tabItemModifiersService';
import type { SaleSellerRef } from '@/services/sales/resolveSaleSeller';
import { generateUuid } from '@/lib/uuid';
import { executeCreateElectronicTicket } from '@/lib/offlineMutations';
import {
  buildCustomerInvoiceInfoFromExternalInvoice,
  canIssueElectronicTicket,
  createElectronicInvoiceParams,
  HACIENDA_CASH_CODE,
  HACIENDA_PAYMENT_CONDITION_CASH,
} from '@/services/hacienda/haciendaHelpers';
import { createElectronicInvoice, getCompanySimplifiedRegime } from '@/services/hacienda/haciendaService';
import { createSaleDataForInvoice } from '@/utils/salesUtils';
import {
  calculateTabSaleTotals,
  type TabTotalsOrgSnapshot,
} from '@/utils/tabTotals';

function hasExternalInvoiceCustomerData(
  invoice?: NonNullable<Tab['externalMetadata']>['invoice'] | null,
): invoice is NonNullable<NonNullable<Tab['externalMetadata']>['invoice']> {
  if (!invoice) return false;
  return Boolean(
    invoice.identification?.trim() && invoice.name?.trim() && invoice.email?.trim(),
  );
}

export type CompleteExternalOrderOrgContext = {
  organizationSlug: string;
  digitalInvoicesEnabled?: boolean;
  currency?: string;
  tabTotalsOrg?: TabTotalsOrgSnapshot;
};

function formatCostaRicaTimestamp(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Costa_Rica',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  const milliseconds = String(date.getUTCMilliseconds()).padStart(3, '0');

  return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}:${values.second}.${milliseconds}-06:00`;
}

const mapRowToTab = (row: Record<string, unknown>, organizationId: string): Tab => {
  const tabItems = (row.tab_items as Record<string, unknown>[] | null) ?? [];

  return {
    id: row.id as string,
    customerName: row.customer_name as string,
    discount: Number(row.discount || 0),
    isOpen: (row.is_open as boolean) ?? true,
    isCredit: false,
    isQuickSale: false,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
    organizationId,
    sourceType: row.source_type as Tab['sourceType'],
    externalOrderStatus: row.external_order_status as Tab['externalOrderStatus'],
    externalOrderId: (row.external_order_id as string) ?? undefined,
    externalMetadata: (row.external_metadata as Tab['externalMetadata']) ?? {},
    ivaDisabled: Boolean(row.iva_disabled),
    serviceChargeDisabled: Boolean(row.service_charge_disabled),
    items: tabItems.map((item) => {
      const category = item.category_id as Record<string, unknown> | null | undefined;

      return {
        id: item.id as string,
        productId: item.product_id as string,
        product: {
          id: item.product_id as string,
          name: item.product_name as string,
          category: item.product_category as string,
          categoryId: (category?.id as string) ?? '',
          productCategory: category
            ? {
                id: category.id as string,
                name: category.name as string,
                printInKitchen: Boolean(category.print_in_kitchen),
                printInBar: Boolean(category.print_in_bar),
                showInSales: true,
                organizationId: (category.organization_id as string) ?? organizationId,
                cabysCode: category.cabys_code as string | undefined,
                cabysTax: category.cabys_tax as number | undefined,
                activityCode: (category.activity_code as string | null | undefined) ?? null,
                createdAt: new Date(),
                updatedAt: new Date(),
              }
            : undefined,
          cost: 0,
          price: Number(item.unit_price),
          stock: 0,
          organizationId,
        },
        quantity: Number(item.quantity),
        unitPrice: Number(item.unit_price),
        discount: Number(item.discount || 0),
        notes: (item.notes as string) ?? undefined,
        modifiers: mapTabItemModifierRows(item.tab_item_modifiers as TabItemModifierRow[] | null),
        weightKg: item.weight_kg != null ? Number(item.weight_kg) : undefined,
      };
    }),
  };
};

const EXTERNAL_ORDER_TAB_SELECT = `
  *,
  tab_items (
    id, product_id, product_name, product_category,
    quantity, unit_price, discount, notes, weight_kg,
    category_id (
      id,
      name,
      print_in_kitchen,
      print_in_bar,
      organization_id,
      cabys_code,
      cabys_tax,
      activity_code
    ),
    ${TAB_ITEM_MODIFIERS_EMBED}
  )
`;

function isDuplicateSaleError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'code' in error &&
    (error as { code: string }).code === '23505'
  );
}

async function resolveExternalOrderPaymentMethod(
  organizationId: string,
  paymentMethodId?: string,
): Promise<{ id: string; defaultPaymentMethodId?: number }> {
  const supabase = getSupabaseClient();

  if (paymentMethodId?.trim()) {
    const { data, error } = await supabase
      .from('payment_methods')
      .select('id, default_payment_method_id')
      .eq('id', paymentMethodId.trim())
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .maybeSingle();
    if (error) throw error;
    if (!data?.id) throw new Error('payment_method_not_found');
    return {
      id: data.id,
      defaultPaymentMethodId: data.default_payment_method_id ?? undefined,
    };
  }

  const { data, error } = await supabase
    .from('payment_methods')
    .select('id, default_payment_method_id')
    .eq('organization_id', organizationId)
    .eq('default_payment_method_id', EXTERNAL_ORDER_PAYMENT_METHOD)
    .eq('is_active', true)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data?.id) throw new Error('external_order_payment_method_not_found');
  return {
    id: data.id,
    defaultPaymentMethodId: data.default_payment_method_id ?? undefined,
  };
}
async function issueExternalOrderFactura(
  sale: Sale,
  order: Tab,
  currency: string,
): Promise<void> {
  const invoiceMeta = order.externalMetadata?.invoice;
  if (!hasExternalInvoiceCustomerData(invoiceMeta)) return;

  const forceCash =
    order.externalMetadata?.orderType === 'delivery' &&
    order.externalMetadata?.paymentLocation === 'counter';

  try {
    const isSimplifiedRegime = await getCompanySimplifiedRegime();
    const customerInfo = buildCustomerInvoiceInfoFromExternalInvoice(invoiceMeta);
    const params = createElectronicInvoiceParams(
      sale,
      customerInfo,
      currency,
      HACIENDA_PAYMENT_CONDITION_CASH,
      isSimplifiedRegime,
      forceCash ? HACIENDA_CASH_CODE : undefined,
    );
    const response = await createElectronicInvoice(params);
    if (!response.success) {
      console.warn('Failed to create external-order FE:', response.error);
      return;
    }
    const invoiceId = response.data?.invoice_id;
    if (!invoiceId) return;
    await salesService.updateSaleInvoiceId(sale.id, invoiceId);
  } catch (error) {
    console.warn('Failed to create external-order FE:', error);
  }
}

async function completeExternalOrder(
  order: Tab,
  organizationId: string,
  paymentMethodId: string | undefined,
  seller: SaleSellerRef,
  orgContext: CompleteExternalOrderOrgContext,
): Promise<void> {
  const totals = calculateTabSaleTotals(order, orgContext.tabTotalsOrg);
  const paymentMethod = await resolveExternalOrderPaymentMethod(
    organizationId,
    paymentMethodId,
  );
  const saleId = generateUuid();
  const currency = orgContext.currency === 'USD' ? 'USD' : 'CRC';
  const serviceChargeForSale = totals.serviceCharge > 0 ? totals.serviceCharge : undefined;
  const serviceChargeRateForSale = totals.serviceChargeRate;
  const ivaForSale = totals.iva > 0 ? totals.iva : undefined;
  const ivaRateForSale = totals.ivaRate;

  try {
    await salesService.createSale({
      saleId,
      tabId: order.id,
      customerName: order.customerName,
      subtotal: totals.subtotal,
      discount: totals.discount,
      serviceCharge: serviceChargeForSale,
      serviceChargeRate: serviceChargeRateForSale,
      iva: ivaForSale,
      ivaRate: ivaRateForSale,
      total: totals.total,
      tip: totals.tip,
      paymentMethodsJson: [{ methodId: paymentMethod.id, amount: totals.total }],
      seller,
      organizationId,
      saleDateIso: new Date().toISOString(),
    });
  } catch (error) {
    if (!isDuplicateSaleError(error)) throw error;
  }

  await tabsService.closeTab(order.id);

  const invoiceMeta = order.externalMetadata?.invoice;
  if (hasExternalInvoiceCustomerData(invoiceMeta)) {
    const saleForInvoice = createSaleDataForInvoice(
      saleId,
      order,
      totals.discount === 100 ? null : totals.discount,
      serviceChargeForSale ?? null,
      serviceChargeRateForSale ?? null,
      totals.tip,
      [
        {
          methodId: paymentMethod.id,
          amount: totals.total,
          defaultPaymentMethodId: paymentMethod.defaultPaymentMethodId,
        },
      ],
      organizationId,
    );
    await issueExternalOrderFactura(saleForInvoice, order, currency);
    return;
  }

  if (
    canIssueElectronicTicket({
      organizationSlug: orgContext.organizationSlug,
      digitalInvoicesEnabled: orgContext.digitalInvoicesEnabled,
      ivaDisabled: order.ivaDisabled,
      defaultPaymentMethodId: paymentMethod.defaultPaymentMethodId,
    })
  ) {
    try {
      await executeCreateElectronicTicket({
        saleId,
        organizationId,
        items: order.items ?? [],
        tabDiscount: totals.discount,
        currency,
        defaultPaymentMethodId: paymentMethod.defaultPaymentMethodId,
        serviceCharge: serviceChargeForSale,
        serviceChargeRate: serviceChargeRateForSale,
        tip: totals.tip,
      });
    } catch (error) {
      // The sale is already recorded and the tab closed; do not block the
      // status transition on the fiscal document.
      console.warn('Failed to create external-order TE:', error);
    }
  }
}

export const externalOrdersService = {
  async getExternalOrders(organizationId: string): Promise<Tab[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('tabs')
      .select(EXTERNAL_ORDER_TAB_SELECT)
      .eq('organization_id', organizationId)
      .eq('source_type', 'external')
      .eq('is_open', true)
      .in('external_order_status', ['pending', 'accepted', 'preparing', 'ready'])
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map((row) => mapRowToTab(row, organizationId));
  },

  async updateOrderStatus(
    orderId: string,
    organizationId: string,
    nextStatus: ExternalOrderStatus,
    paymentMethodId?: string,
    seller?: SaleSellerRef,
    orgContext?: CompleteExternalOrderOrgContext,
    estimatedPrepMinutes?: number,
  ): Promise<void> {
    const supabase = getSupabaseClient();

    const { data: row, error: fetchError } = await supabase
      .from('tabs')
      .select(EXTERNAL_ORDER_TAB_SELECT)
      .eq('id', orderId)
      .eq('organization_id', organizationId)
      .eq('source_type', 'external')
      .single();

    if (fetchError) throw fetchError;

    const currentStatus = row.external_order_status as ExternalOrderStatus | null;
    if (!currentStatus) {
      throw new Error('invalid_order_state');
    }
    if (!isValidStatusTransition(currentStatus, nextStatus)) {
      throw new Error('invalid_status_transition');
    }

    if (nextStatus === EXTERNAL_ORDER_STATUSES.COMPLETED) {
      if (!seller) throw new Error('seller_not_found');
      if (!orgContext?.organizationSlug) throw new Error('organization_context_missing');
      const order = mapRowToTab(row, organizationId);
      await completeExternalOrder(
        order,
        organizationId,
        paymentMethodId,
        seller,
        orgContext,
      );
    }

    const updatePayload: Record<string, unknown> = {
      external_order_status: nextStatus,
      updated_at: new Date().toISOString(),
    };
    if (nextStatus === EXTERNAL_ORDER_STATUSES.ACCEPTED) {
      if (!Number.isInteger(estimatedPrepMinutes) || estimatedPrepMinutes <= 0) {
        throw new Error('invalid_estimated_prep_minutes');
      }
      updatePayload.external_metadata = {
        ...((row.external_metadata as Record<string, unknown> | null) ?? {}),
        acceptedAt: formatCostaRicaTimestamp(new Date()),
        estimatedPrepMinutes,
      };
    }
    if (nextStatus === EXTERNAL_ORDER_STATUSES.CANCELLED) {
      updatePayload.is_open = false;
    }

    const { error: updateError } = await supabase
      .from('tabs')
      .update(updatePayload)
      .eq('id', orderId)
      .eq('organization_id', organizationId);

    if (updateError) throw updateError;
  },
};
